// Shared types moved to @autoriz/shared/files (feature 240).
// This module keeps only frontend-specific UI types + component prop shapes.
export type {
  AllowedMimeType,
  UploadTicketRequest,
  UploadTicket,
  FileUploadNotification,
} from '@autoriz/shared/files'
// `AllowedMimeTypes` (plural record shape) is now inferred from the shared const
// in mime.ts via `typeof ALLOWED_MIME_TYPES`. Consumers that previously referenced
// the local `AllowedMimeTypes` interface should use `typeof ALLOWED_MIME_TYPES`
// or import the shared `AllowedMimeType` union.

// File upload states — frontend-only UI state machine
export const FileUploadStatus = {
  IDLE: 'IDLE',
  VALIDATING: 'VALIDATING',
  GENERATING_TICKET: 'GENERATING_TICKET',
  UPLOADING: 'UPLOADING',
  PROCESSING: 'PROCESSING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
} as const

export type FileUploadStatusType = typeof FileUploadStatus[keyof typeof FileUploadStatus]

// File upload progress information — frontend UI concern
export interface FileUploadProgress {
  fileId: string
  filename: string
  status: FileUploadStatusType
  progress: number
  error?: string
  uploadedSize?: number
  totalSize?: number
}

// Import AllowedMimeType locally for the prop shapes defined below.
import type { AllowedMimeType } from '@autoriz/shared/files'

// File validation result
export interface FileValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

// File upload result
export interface FileUploadResult {
  success: boolean
  fileId?: string
  filename: string
  error?: string
  progress?: FileUploadProgress
  /**
   * Set when the backend detected an archive (ZIP/RAR/7Z/TAR family) and
   * enqueued an extraction job. Consumers use this to show a progress
   * modal polling the `jobs` row — the source archive file is disabled
   * once extraction completes, so the normal "file uploaded" UX would be
   * misleading.
   */
  archiveJobId?: string | null
}

// File upload configuration from backend
export interface FileUploadConfiguration {
  allowedMimeTypes: Record<AllowedMimeType, { ext: string; maxSize: number }>
  usageSizeLimits: Record<string, number>
  pathConstraints: {
    allowedPrefixes: readonly string[]
    maxPathLength: number
    filenamePattern: string
    maxFilenameLength: number
  }
  uploadConstraints: {
    presignedUrlExpirySeconds: number
    jwtExpirySeconds: number
    maxPendingUploadsPerUser: number
    maxUploadsPerProjectPerHour: number
  }
  usageTypes: Record<string, UsageTypeMetadata>
  sections: Record<string, SectionMetadata>
}

// Usage type metadata for UI
export interface UsageTypeMetadata {
  usage: string
  label: string
  description: string
  allowedTypes: AllowedMimeType[]
  maxSize: number
  section: string
  examples: string[]
}

// Section metadata
export interface SectionMetadata {
  section: string
  label: string
  description: string
}

// Upload component props interfaces
export interface BaseUploadProps {
  projectId?: number
  urbanAnalysisId?: number
  usage?: string
  allowedUsageTypes?: string[]
  onUploadStart?: (file: File) => void
  onUploadProgress?: (progress: FileUploadProgress) => void
  onUploadComplete?: (result: FileUploadResult) => void
  onUploadError?: (error: string, file?: File) => void
  disabled?: boolean
  maxFileSize?: number
  acceptedMimeTypes?: AllowedMimeType[]
  multiple?: boolean
  sanitizeFilenames?: boolean
}

export interface UploadButtonProps extends BaseUploadProps {
  children?: React.ReactNode
  buttonProps?: Record<string, unknown>
}

export interface DropUploadProps extends BaseUploadProps {
  children?: React.ReactNode
  className?: string
  height?: number | string
  showUploadIcon?: boolean
  uploadText?: string
  dragActiveText?: string
}

// Upload hook return type
export interface UseFileUploadReturn {
  uploadFile: (file: File, usage?: string) => Promise<FileUploadResult>
  uploadFiles: (files: File[], usage?: string) => Promise<FileUploadResult[]>
  validateFile: (file: File, usage?: string) => FileValidationResult
  isUploading: boolean
  uploadProgress: Record<string, FileUploadProgress>
  errors: string[]
  clearErrors: () => void
  configuration: FileUploadConfiguration | null
  isLoadingConfig: boolean
  refreshConfig: () => Promise<void>
}

// Configuration hook return type
export interface UseFileUploadConfigReturn {
  configuration: FileUploadConfiguration | null
  isLoading: boolean
  error: string | null
  refreshConfig: () => Promise<void>
  getUsageMetadata: (usage: string) => UsageTypeMetadata | null
  getSectionMetadata: (section: string) => SectionMetadata | null
  getAvailableUsageTypes: (section?: string) => UsageTypeMetadata[]
}
